/**
 * PrivilegedMfaBanner — app-wide notice that the signed-in account holds a role
 * requiring two-factor authentication and has not enrolled yet.
 *
 * Persistent by design: it is a required, time-bound action, so unlike a
 * dismissible notification it stays until the user either enrolls or their
 * access is enforced. It reads `user.privilegedMfa` (populated on `/auth/me`),
 * so it renders app-wide without touching the settings page. Enforcement is
 * server-side; this only informs and links to setup.
 *
 * Shown only when a privileged role is under an active gate AND the user is not
 * enrolled. Two tones:
 *   - grace, deadline in the future → amber countdown ("N days left").
 *   - enforced, or the personal deadline has passed → red "access is limited".
 */

import { Link } from 'react-router-dom';
import { ShieldAlert } from 'lucide-react';
import { useAuth } from '@/auth/hooks/useAuth';

export function PrivilegedMfaBanner() {
  const { user } = useAuth();
  const gate = user?.privilegedMfa;

  // Nothing to show: no gate obligation, or already enrolled.
  if (!gate || !gate.required || gate.enrolled) return null;

  const deadlineMs = gate.deadline ? Date.parse(gate.deadline) : null;
  const past = gate.mode === 'enforced' || (deadlineMs != null && Date.now() >= deadlineMs);
  const days = gate.daysRemaining;

  const timeLeft =
    days == null ? null : days === 0 ? 'less than a day' : `${days} day${days === 1 ? '' : 's'}`;

  const message = past
    ? 'Two-factor authentication is required for your account role. Administrative access is limited until you turn it on.'
    : timeLeft != null
      ? `Two-factor authentication is required for your account role. You have ${timeLeft} left to set it up before administrative access is paused.`
      : 'Two-factor authentication is required for your account role. Set it up soon to keep administrative access.';

  const tone = past
    ? 'bg-destructive/10 border-destructive/30 text-destructive'
    : 'bg-amber-500/10 border-amber-500/30 text-amber-800 dark:text-amber-300';

  return (
    <div
      role="alert"
      className={`flex flex-wrap items-center justify-center gap-x-3 gap-y-1 border-b px-4 py-2 text-sm ${tone}`}
    >
      <ShieldAlert className="h-4 w-4 flex-shrink-0" />
      <span>{message}</span>
      <Link to="/auth/settings" className="font-semibold underline underline-offset-2">
        Set up two-factor authentication
      </Link>
    </div>
  );
}
